/**
 * **Check-in** (F3): the find box, what just happened, the results and the card's hands — the
 * station's main column once someone is at the desk.
 */
import { type ReactNode } from 'react';
import { formatCountDay } from '@cider/ui';
import { api, sentence } from '../../shell/api.js';
import { loadDay, saveDay } from './boothOffline.js';
import { CoverFor, ReassignOne, UndoOne, WhoIsWorking } from './BoothDesk.js';
import {
  type Found,
  GROUP,
  HOUSE,
  PersonPlusIcon,
  SCAN,
  ScanIcon,
  hours,
  matchedBy,
  stable,
} from './BoothShared.js';
import { PersonCard } from './BoothCard.js';
import { ScanCards } from './BoothScanCards.js';
import { useBooth } from './BoothState.js';
import { PendingWalkUp, WalkUp } from './BoothWalkUp.js';

/** The station’s main column, in the order it is drawn. */
export function BoothCheckIn(): ReactNode {
  const { signingIn } = useBooth();
  if (signingIn) return null;
  return (
    <>
      <FindBox />
      <PendingWalkUp />
      <ScanCards />
      <DoneStrip />
      <ReadyCards />
      <Results />
      <Hands />
      <WalkUp />
    </>
  );
}

/** The one box: a scan, a name, an old name, a phone — and New person. */
export function FindBox(): ReactNode {
  const {
    q,
    setQ,
    found,
    picked,
    setPicked,
    setWalkUp,
    setVisitorOpen,
    setLeft,
    setAlready,
    setDone,
    setTag,
    setGroup,
    setGroupPick,
    house,
    setHouse,
    setHousePick,
    setBusy,
    guest,
    search,
    setDayCache,
    goneOffline,
    desk,
    load,
    enqueue,
    show,
    pickResult,
    startWalkUp,
    reset,
    checkIn,
  } = useBooth();
  if (!desk) return null;
  return (
    <>
      <form
        className="booth__find"
        onSubmit={(e) => {
          e.preventDefault();
          // The family page's QR (Rebuild 29, state 6): the household card.
          if (HOUSE.test(q.trim())) {
            const code = q.trim();
            api<{
              name: string;
              familyId: string | null;
              says: string;
              members: (Found & { needsWaiver: boolean; minor?: boolean })[];
            }>('GET', `/volunteers/booth/household?code=${encodeURIComponent(code)}`)
              .then((r) => {
                setPicked(null);
                setHouse({ code, at: new Date(), ...r });
                // The household on its own guest screen (G06): they tap who is here.
                if (guest?.on) show('household', undefined, null, undefined, code);
                setHousePick(
                  new Set(
                    r.members
                      .filter((m) => m.today.some((t) => t.state === 'expected'))
                      .map((m) => m.personId),
                  ),
                );
                setQ('');
              })
              .catch((err: unknown) => {
                setDone(sentence(err));
                setQ('');
              });
            return;
          }
          // A group leader's QR (ON10 item 4): the group card.
          if (GROUP.test(q.trim())) {
            const code = q.trim();
            api<{
              name: string;
              leader: string;
              says: string;
              members: (Found & { needsWaiver: boolean })[];
            }>('GET', `/volunteers/booth/group?code=${encodeURIComponent(code)}`)
              .then((r) => {
                setGroup({ code, at: new Date(), ...r });
                setGroupPick(
                  new Set(
                    r.members
                      .filter((m) => !m.needsWaiver && m.today.some((t) => t.state === 'expected'))
                      .map((m) => m.personId),
                  ),
                );
                setQ('');
              })
              .catch((err: unknown) => {
                setDone(sentence(err));
                setQ('');
              });
            return;
          }
          // A tag's QR, typed by the scanner (7c): the check-out, at any station.
          if (SCAN.test(q.trim())) {
            setBusy(true);
            api<{
              kind: 'out' | 'already';
              says: string;
              personId: string;
              shiftId: string;
              name: string;
              inAt?: string;
              area?: string;
            }>('POST', '/volunteers/booth/scan', { code: q.trim() })
              .then((r) => {
                setTag(null);
                if (r.kind === 'already') {
                  // Scanned twice (state 15): their card, with when they came in — the
                  // check-out and the reprint are on it if they are actually leaving.
                  reset();
                  setAlready({ at: r.inAt ?? null, personId: r.personId });
                  api<{ rows: Found[] }>(
                    'GET',
                    `/volunteers/booth/find?q=${encodeURIComponent(r.name)}`,
                  )
                    .then((f) => {
                      const row = f.rows.find((x) => x.personId === r.personId);
                      if (row) pickResult(row);
                    })
                    .catch(() => undefined);
                  return;
                }
                setDone(`${r.name}: ${r.says}`);
                show('thanks', r.personId, r.shiftId);
                reset();
                // The check-out (state 23): what they earned, and the ways out.
                setLeft({
                  name: r.name,
                  area: r.area ?? null,
                  inAt: r.inAt ?? null,
                  outAt: new Date().toISOString(),
                });
                load();
              })
              .catch((err: unknown) => {
                if (goneOffline(err)) {
                  // No network (7g): the tag is read against the day kept here.
                  const code = q.trim().replace(SCAN, '').toUpperCase();
                  const t = loadDay()?.tags.find((x) => x.code === code);
                  const who = t
                    ? loadDay()?.people.find((p) => p.personId === t.personId)?.name
                    : undefined;
                  if (t?.inAt && Date.now() - new Date(t.inAt).getTime() < 30 * 60_000)
                    setDone(`${who ?? 'They'} are already in — nothing changed.`);
                  else {
                    enqueue({
                      kind: 'scan',
                      code,
                      personId: t?.personId ?? null,
                      shiftId: t?.shiftId ?? null,
                    });
                    setDone(
                      `${who ?? 'The tag'}: checked out — queued with its time; it seals when the network is back.`,
                    );
                    if (t) {
                      const d = loadDay();
                      if (d) {
                        for (const p of d.people)
                          if (p.personId === t.personId)
                            for (const x of p.today) if (x.shiftId === t.shiftId) x.state = 'out';
                        saveDay(d);
                        setDayCache(d);
                      }
                    }
                  }
                  setQ('');
                  return;
                }
                setDone(sentence(err));
                setQ('');
              })
              .finally(() => {
                setBusy(false);
              });
            return;
          }
          // Enter on a card is the big button (Rebuild 29).
          if (!q.trim() && (picked || house)) {
            document.querySelector<HTMLButtonElement>('[data-testid="booth-big"]')?.click();
            return;
          }
          const only = found?.length === 1 ? found[0] : undefined;
          const shift = only?.today.find((s) => s.state === 'expected');
          if (only && shift && !only.quiet) checkIn(only, shift, false);
          else if (only) {
            setPicked(only);
            if (only.personId !== desk.personId)
              show('confirm', only.personId, only.today[0]?.shiftId ?? null);
          }
        }}
      >
        <span className="booth__searchbox">
          <ScanIcon />
          <input
            ref={search}
            className="booth__search"
            aria-label="Find a volunteer"
            placeholder="Scan a code, or type a name, an old name, a phone"
            autoFocus
            autoComplete="off"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPicked(null);
              setWalkUp(false);
              setVisitorOpen(false);
              setDone(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') reset();
              // 1 – 5 pick a result — while the box holds a name, not a phone's digits.
              if (/^[1-5]$/.test(e.key) && found?.length && !picked && /[a-z]/i.test(q)) {
                const p = stable(found)[Number(e.key) - 1];
                if (p) {
                  e.preventDefault();
                  pickResult(p);
                }
              }
            }}
          />
        </span>
        <button
          type="button"
          className="booth__btn"
          onClick={() => {
            startWalkUp();
          }}
        >
          <PersonPlusIcon />
          New person
        </button>
      </form>
    </>
  );
}

/** What just happened, and the tag — printed, skipped, or at the booth. */
export function DoneStrip(): ReactNode {
  const { printerOut, done, setDone, tag, setTag, tablet, usesTags } = useBooth();
  return (
    <>
      {done && tag && printerOut ? (
        <div className="booth__found" data-testid="booth-printfail">
          <div className="booth__banner booth__banner--bad" role="alert">
            <b>THIS STATION’S TAG PRINTER IS OUT</b>
            <span>{tag.name} is checked in. Their tag did not print here.</span>
          </div>
          <section className="booth__card">
            <div className="booth__cardhead">WHILE THE ROLL IS CHANGED</div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Their tag prints at another station</b>
                <span className="booth__rowb">by name · Print the tag again</span>
              </span>
              <span className="booth__rowc">
                The check-in is done; any station prints the same tag again. Say the printer is back
                from the Station menu once the roll is in.
              </span>
              <span className="booth__chip booth__chip--info">ANOTHER STATION</span>
            </div>
          </section>
          <div className="booth__bigrow">
            <button
              type="button"
              className="booth__big booth__big--navy"
              data-testid="booth-big"
              onClick={() => {
                setDone(
                  `${tag.name}: at the next station, find them by name and press Print the tag again.`,
                );
                setTag(null);
              }}
            >
              Print at another station
              <span className="booth__bigsub">the next station on the desk</span>
            </button>
            <button
              type="button"
              className="booth__side-act"
              onClick={() => {
                setDone(`${tag.name} is in without a tag.`);
                setTag(null);
              }}
            >
              Skip the tag
            </button>
          </div>
        </div>
      ) : done ? (
        <div className="booth__done" role="status" data-testid="booth-done">
          <span>{done}</span>
          {tag && !usesTags(tag.area) ? (
            <span className="booth__fine" data-testid="booth-no-tag">
              The {tag.area} does not use tags.
            </span>
          ) : tag && tablet ? (
            <span className="booth__fine" data-testid="booth-tag-at-booth">
              Your tag is at the booth.
            </span>
          ) : tag ? (
            <button
              type="button"
              className="booth__btn"
              onClick={() => {
                window.print();
              }}
            >
              Print the tag
            </button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** Nothing open: how this desk works. */
export function ReadyCards(): ReactNode {
  const {
    found,
    picked,
    walkUp,
    left,
    reprinted,
    group,
    house,
    houseDone,
    panel,
    pending,
    startWalkUp,
  } = useBooth();
  return (
    <>
      {!found &&
      !picked &&
      !walkUp &&
      !group &&
      !house &&
      !houseDone &&
      !reprinted &&
      !left &&
      !pending &&
      !panel ? (
        <>
          <section className="booth__card" data-testid="booth-ready">
            <div className="booth__cardhead">
              HOW THIS DESK WORKS
              <span className="booth__cardsub">three ways in, one card</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Scan</b>
                <span className="booth__rowb">
                  the QR on the family page, a printed pass, or a tag
                </span>
              </span>
              <span className="booth__rowc">
                The scanner types into the box. You press nothing. A household lands on the
                household card; a tag is a check-out.
              </span>
              <span className="booth__chip booth__chip--ok">NO TAP</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Search</b>
                <span className="booth__rowb">a name, an old name, a phone</span>
              </span>
              <span className="booth__rowc">Results as you type, today’s shifts first.</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>New person</b>
                <span className="booth__rowb">the festival does not know them yet</span>
              </span>
              <span className="booth__rowc">
                You type a name and a phone. They finish the rest on the guest screen.
              </span>
              <button type="button" className="booth__act" onClick={startWalkUp}>
                Walk-up
              </button>
            </div>
            <div className="booth__cardfoot">
              The moment a person is found, the guest screen shows their page. Nothing to send.
            </div>
          </section>
          <section className="booth__card booth__card--sky">
            <div className="booth__cardhead">
              ON EVERY CARD
              <span className="booth__cardsub">three buttons beside Check in</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Someone else is working it</b>
                <span className="booth__rowb">the wife signed up, the husband is here</span>
              </span>
              <span className="booth__rowc">
                Household first, then anyone. The shift moves; the signup stays.
              </span>
              <span className="booth__chip booth__chip--info">SWAP</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Reassign</b>
                <span className="booth__rowb">a different shift or area, now or mid-shift</span>
              </span>
              <span className="booth__chip booth__chip--info">MOVE</span>
            </div>
            <div className="booth__row">
              <span className="booth__rowa">
                <b>Cover for someone</b>
                <span className="booth__rowb">a swap across households</span>
              </span>
              <span className="booth__chip booth__chip--info">COVER</span>
            </div>
          </section>
        </>
      ) : null}
    </>
  );
}

/** Results as you type. */
export function Results(): ReactNode {
  const { q, found, findSays, picked, walkUp, pickResult } = useBooth();
  return (
    <>
      {found && !picked && !walkUp ? (
        <ul className="booth__results" data-testid="booth-results">
          {found.length === 0 ? (
            <li className="booth__result booth__result--none">
              <strong>No one by that name</strong>
              <span>{findSays}</span>
            </li>
          ) : (
            stable(found).map((p) => (
              <li key={p.personId}>
                <button
                  type="button"
                  className="booth__result"
                  onClick={() => {
                    pickResult(p);
                  }}
                >
                  <strong>{p.name}</strong>
                  <span className="booth__resultwhy">{matchedBy(p, q)}</span>
                  <span className="booth__resultday">
                    {p.today[0]
                      ? `${p.today[0].area} · ${hours(p.today[0].startsAt, p.today[0].endsAt)}`
                      : p.otherDays[0]
                        ? `not on today · ${formatCountDay(p.otherDays[0].day)}`
                        : 'no shift this year'}
                  </span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </>
  );
}

/** The card’s other hands — undo, reassign, cover, swap — or the card itself. */
export function Hands(): ReactNode {
  const {
    picked,
    setCovering,
    setDone,
    setTag,
    busy,
    setOperator,
    desk,
    opName,
    why,
    setWhy,
    hand,
    setHand,
    show,
    act,
    day,
    placeChoices,
    checkIn,
    workedBy,
  } = useBooth();
  if (!desk) return null;
  return (
    <>
      {picked && hand?.kind === 'undo' ? (
        <UndoOne
          who={picked}
          shift={picked.today.find((x) => x.shiftId === hand.shiftId) ?? null}
          choices={placeChoices.filter((x) => x.shiftId !== hand.shiftId && x.short > 0)}
          operator={opName}
          onOperator={setOperator}
          desk={desk.name}
          why={why}
          onWhy={setWhy}
          busy={busy}
          onUndo={(then) => {
            const who = picked;
            act(
              '/volunteers/booth/undo',
              { shiftId: hand.shiftId, personId: who.personId, name: opName, reason: why },
              (res) => {
                setTag(null);
                setDone(`${who.name}: ${res.says}`);
                show('welcome');
                if (then) checkIn({ ...who, today: [] }, then, true);
              },
            );
          }}
          onBack={() => {
            setHand(null);
          }}
        />
      ) : picked && hand?.kind === 'reassign' ? (
        <ReassignOne
          who={picked}
          from={picked.today.find((x) => x.shiftId === hand.shiftId) ?? null}
          choices={placeChoices.filter((x) => x.shiftId !== hand.shiftId)}
          operator={opName}
          onOperator={setOperator}
          desk={desk.name}
          busy={busy}
          onReassign={(toShiftId, reason) => {
            const who = picked;
            act(
              '/volunteers/booth/reassign',
              {
                fromShiftId: hand.shiftId,
                personId: who.personId,
                toShiftId,
                name: opName,
                reason: reason.trim() ? reason : null,
              },
              (res) => {
                const x = res as {
                  says: string;
                  area?: string;
                  startsAt?: string;
                  endsAt?: string;
                  tag?: string;
                };
                setDone(`${who.name}: ${x.says}`);
                if (x.area && x.startsAt && x.endsAt)
                  setTag({
                    name: who.name,
                    area: x.area,
                    hours: hours(x.startsAt, x.endsAt),
                    day,
                    code: x.tag,
                  });
                show('tag', who.personId, toShiftId);
              },
            );
          }}
          onBack={() => {
            setHand(null);
          }}
        />
      ) : picked && hand?.kind === 'cover' ? (
        <CoverFor
          worker={picked}
          operator={opName}
          onOperator={setOperator}
          desk={desk.name}
          busy={busy}
          onCover={(covered, sh) => {
            setCovering(covered.name);
            if (sh.signupId) workedBy(sh.signupId, picked, sh, 'cover', true);
          }}
          onChoose={(covered) => {
            setCovering(covered?.name ?? null);
          }}
          onBack={() => {
            setCovering(null);
            setHand(null);
          }}
        />
      ) : picked && hand?.kind === 'swap' ? (
        <WhoIsWorking
          signer={picked}
          shift={picked.today.find((x) => x.shiftId === hand.shiftId) ?? null}
          operator={opName}
          onOperator={setOperator}
          desk={desk.name}
          busy={busy}
          onPick={(worker) => {
            const sh = picked.today.find((x) => x.shiftId === hand.shiftId);
            if (sh) workedBy(hand.signupId, worker, sh, 'swap');
          }}
          onBack={() => {
            setHand(null);
          }}
        />
      ) : picked ? (
        <PersonCard />
      ) : null}
    </>
  );
}
